import { FC, useEffect, useState } from 'react';
import { useAppSelector } from '../../redux/hooks';
import { selectedDiagnosticReportsFilter } from '../../redux/reducers/filterDiagnosticReport';
import { useQueryLoader } from 'react-relay';
import { DiagnosticsReport } from './DiagnosticsReport';
import {
  getDiagnosticStatusByClassReport,
  getStudentsCompletedDiagnosticInfo,
  getStudentsDiganosticReportTableInfo,
  getStudentsNotCompletedDiagnosticInfo,
} from '../../graphQLOperations/Diagnostics';
import { DiagnosticsCompletedStudentsQuery } from '../../graphQLOperations/__generated__/DiagnosticsCompletedStudentsQuery.graphql';
import { TabPanel, TabView } from 'primereact/tabview';
import { DiagnosticsGetStudentsTableQuery } from '../../graphQLOperations/__generated__/DiagnosticsGetStudentsTableQuery.graphql';
import { SkillPerformanceByClass } from './SkillPerformanceByClass';
import { StudentsCompletedGateWrapper } from './StudentsCompletedGateWrapper';
import { DiagnosticsStatusByClassReportQuery } from '../../graphQLOperations/__generated__/DiagnosticsStatusByClassReportQuery.graphql';

export const DiagnosticsReportWrapper: FC = () => {
  const reportFilters = useAppSelector(selectedDiagnosticReportsFilter);
  const [refetchKey, setRefetchKey] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [studentsCompletedQueryRef, loadStudentsCompletedQuery] = useQueryLoader<DiagnosticsCompletedStudentsQuery>(
    getStudentsCompletedDiagnosticInfo,
  );
  const [studentsNotCompletedQueryRef, loadStudentsNotCompletedQuery] =
    useQueryLoader<DiagnosticsCompletedStudentsQuery>(getStudentsNotCompletedDiagnosticInfo);
  const [studentsInProgressQueryRef, loadStudentsInProgressQuery] = useQueryLoader<DiagnosticsStatusByClassReportQuery>(
    getDiagnosticStatusByClassReport,
  );
  const [studentsTableInfoQueryRef, loadStudentsTableInfoQuery] = useQueryLoader<DiagnosticsGetStudentsTableQuery>(
    getStudentsDiganosticReportTableInfo,
  );

  const loadDiagnosticsInfo = () => {
    const classIds = reportFilters.classes.map(cl => cl.id) || null;
    loadStudentsNotCompletedQuery({ classIds }, { fetchPolicy: 'network-only' });
    loadStudentsCompletedQuery({ classIds }, { fetchPolicy: 'network-only' });
    loadStudentsInProgressQuery({ filter: { classIds } }, { fetchPolicy: 'network-only' });
    loadStudentsTableInfoQuery(
      { input: { classIds: [classIds[0]], roadmapId: reportFilters.roadmap?.id || '' } },
      { fetchPolicy: 'network-only' },
    );
  };

  useEffect(() => {
    loadDiagnosticsInfo();
  }, [reportFilters, refetchKey]);

  const activateTab = (index: number) => {
    setActiveIndex(index);
  };

  const handleTabChange = (e: { index: number }) => {
    activateTab(e.index);
  };

  const onHeaderKeyDown = (index: number) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      activateTab(index);
    }
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-y-4 tw-w-full">
      <TabView className="tw-w-full nc_tabView-new" activeIndex={activeIndex} onTabChange={handleTabChange}>
        <TabPanel
          header="Students results"
          pt={{
            headerAction: {
              id: 'dgStudent',
              role: 'tab',
              'aria-selected': activeIndex === 0,
              'aria-controls': 'panel-students',
              onKeyDown: onHeaderKeyDown(0),
              className: 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-400', // ✅ foco visible
            },
            content: {
              id: 'panel-students',
              role: 'tabpanel',
              'aria-labelledby': 'dgStudent',
            },
          }}
        >
          <DiagnosticsReport
            completedQueryRef={studentsCompletedQueryRef}
            notCompletedQueryRef={studentsNotCompletedQueryRef}
            inProgressQueryRef={studentsInProgressQueryRef}
          />
        </TabPanel>

        <TabPanel
          header="Skill performance by class"
          pt={{
            headerAction: {
              id: 'dgSkillPerformance',
              tabIndex: 0,
              role: 'tab',
              'aria-selected': activeIndex === 1,
              'aria-controls': 'panel-skill-performance',
              onKeyDown: onHeaderKeyDown(1),
              className: 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-400',
            },
            content: {
              id: 'panel-skill-performance',
              role: 'tabpanel',
              'aria-labelledby': 'dgSkillPerformance',
            },
          }}
        >
          <SkillPerformanceByClass
            completedQueryRef={studentsCompletedQueryRef}
            notCompletedQueryRef={studentsNotCompletedQueryRef}
            reportFilters={reportFilters}
          />
        </TabPanel>
      </TabView>

      {activeIndex === 0 && (
        <StudentsCompletedGateWrapper
          studentsTableQueryRef={studentsTableInfoQueryRef}
          inProgressQueryRef={studentsInProgressQueryRef}
          setRefetchKey={setRefetchKey}
        />
      )}
    </div>
  );
};
